import { useRef } from 'react';
import { CheckCircle2, FileText, Upload, X } from 'lucide-react';
import type { ShippingDocument } from '@/apis/warehouse';

const ACCEPTED_TYPES =
  '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const IMAGE_EXTENSION_RE = /\.(png|jpe?g)$/i;

interface Props {
  label: string;
  document: ShippingDocument | undefined;
  onUpload: (file: File) => void;
  onDelete: () => void;
  isUploading?: boolean;
  isDeleting?: boolean;
  canAct?: boolean;
}

/** Compact icon-tile upload slot — one of a fixed 3 (Invoice/Label/Bill of Lading), not a free-form dropzone. */
export function ShippingDocumentSlot({
  label,
  document,
  onUpload,
  onDelete,
  isUploading,
  isDeleting,
  canAct = true,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isImage = document ? IMAGE_EXTENSION_RE.test(document.name) : false;
  const disabled = !canAct || isUploading || isDeleting;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = '';
  };

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative">
        <button
          type="button"
          onClick={() => !document && !disabled && fileInputRef.current?.click()}
          disabled={disabled}
          className={`flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border-2 transition-colors ${
            document
              ? 'border-success bg-success-light'
              : 'cursor-pointer border-dashed border-primary-s500/40 bg-info-light hover:bg-primary-s50'
          } ${disabled && !document ? 'cursor-not-allowed opacity-60' : ''}`}
        >
          {document ? (
            isImage ? (
              <a href={document.url} target="_blank" rel="noopener noreferrer" className="block h-full w-full">
                <img src={document.url} alt={label} className="h-full w-full object-cover" />
              </a>
            ) : (
              <a
                href={document.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-full w-full flex-col items-center justify-center gap-1 text-success"
              >
                <FileText size={22} />
              </a>
            )
          ) : isUploading ? (
            <span className="text-xs font-medium text-lightGray">…</span>
          ) : (
            <Upload size={20} className="text-primary-s500" />
          )}
        </button>

        {document && (
          <CheckCircle2
            size={16}
            className="absolute -right-1 -top-1 rounded-full bg-white text-success"
          />
        )}

        {document && canAct && (
          <button
            type="button"
            onClick={onDelete}
            disabled={isDeleting}
            aria-label={`Remove ${label}`}
            className="absolute -left-1 -bottom-1 flex h-5 w-5 items-center justify-center rounded-full border border-smoke bg-white text-silver hover:text-danger disabled:opacity-50"
          >
            <X size={11} />
          </button>
        )}
      </div>

      <span className="text-xs font-normal text-lightGray">{label}</span>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
